import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatDate, formatApiError, formatTZS } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Search, Upload, ClipboardCheck, Loader2, CheckCircle2, Users } from "lucide-react";

export default function CheckInPage() {
  const [sp] = useSearchParams();
  const [refInput, setRefInput] = useState(sp.get("ref") || "");
  const [booking, setBooking] = useState(null);
  const [settings, setSettings] = useState(null);
  const [availableUnits, setAvailableUnits] = useState([]);
  const [unitId, setUnitId] = useState("");
  const [numGuests, setNumGuests] = useState("1");
  const [fields, setFields] = useState({});
  const [idPhotoPath, setIdPhotoPath] = useState("");
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [confirmingPayment, setConfirmingPayment] = useState(false);

  useEffect(() => { (async () => { const { data } = await api.get("/settings"); setSettings(data); })(); }, []);

  const findBooking = async (e) => {
    e?.preventDefault();
    if (!refInput.trim()) return;
    try {
      const { data } = await api.get(`/bookings/reference/${refInput.trim().toUpperCase()}`);
      if (data.status === "cancelled") { toast.error("Booking cancelled"); return; }
      setBooking(data);
      const initial = { full_name: data.guest_name, phone: data.guest_phone, email: data.guest_email };
      setFields(initial);
      setIdPhotoPath("");
      setNumGuests(String(data.adults || 1));
      // fetch available units for this type on those dates
      const { data: units } = await api.get(`/admin/room-units?room_type_id=${data.room_type_id}`);
      // Filter: is_active && !manual_blocked && not conflicting for these dates
      const bookings = (await api.get(`/admin/bookings`)).data;
      const busy = new Set();
      bookings.forEach((b) => {
        if (b.id === data.id) return;
        if (!["booked", "checked_in"].includes(b.status)) return;
        if (!b.room_unit_id) return;
        if (b.check_in_date < data.check_out_date && b.check_out_date > data.check_in_date) busy.add(b.room_unit_id);
      });
      const free = units.filter((u) => u.is_active && !u.manual_blocked && !busy.has(u.id) && (u.housekeeping_status || "ready") === "ready");
      setAvailableUnits(free);
      setUnitId(data.room_unit_id || free[0]?.id || "");
    } catch (e) { toast.error(formatApiError(e)); setBooking(null); }
  };

  useEffect(() => { if (refInput) findBooking(); }, []);

  const uploadPhoto = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("folder", "guest-ids");
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setIdPhotoPath(data.path);
      toast.success("ID photo uploaded");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setUploading(false); }
  };

  const confirmPayment = async () => {
    setConfirmingPayment(true);
    try {
      await api.post(`/admin/bookings/${booking.id}/confirm-payment`);
      setBooking({ ...booking, payment_status: "paid" });
      toast.success("Payment confirmed");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setConfirmingPayment(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!booking) return;
    if (!unitId) return toast.error("Assign a room number");
    if (!idPhotoPath) return toast.error("Upload the guest's ID photo");
    setSubmitting(true);
    try {
      await api.post(`/admin/bookings/${booking.id}/checkin`, {
        room_unit_id: unitId,
        num_guests: Number(numGuests),
        id_photo_path: idPhotoPath,
        fields,
      });
      toast.success("Guest checked in ✓");
      setBooking(null); setRefInput(""); setFields({}); setIdPhotoPath("");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSubmitting(false); }
  };

  const enabledFields = (settings?.check_in_fields || []).filter((f) => f.enabled);

  return (
    <div className="space-y-6" data-testid="checkin-page">
      <div>
        <h1 className="font-heading text-3xl text-[color:var(--deep)]">Digital check-in</h1>
        <p className="text-sm text-[color:var(--muted-text)] mt-1">Assign a room, capture guest details and ID photo.</p>
      </div>

      <Card>
        <CardContent className="p-6">
          <form onSubmit={findBooking} className="flex gap-2">
            <Input placeholder="Enter booking reference e.g. BR7A3F2C" value={refInput} onChange={(e) => setRefInput(e.target.value)} data-testid="checkin-ref-input" />
            <Button type="submit" className="bg-[color:var(--brand)] hover:bg-[color:var(--brand-dark)]" data-testid="checkin-lookup-btn"><Search className="w-4 h-4 mr-1" /> Find</Button>
          </form>
        </CardContent>
      </Card>

      {booking && (
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between border-b pb-4 mb-4 flex-wrap gap-3">
              <div>
                <p className="text-xs uppercase tracking-wider text-[color:var(--brand)]">Booking</p>
                <p className="font-heading text-xl text-[color:var(--deep)]">{booking.reference} · {booking.room_type_name}</p>
                <p className="text-xs text-[color:var(--muted-text)] mt-1">{formatDate(booking.check_in_date)} → {formatDate(booking.check_out_date)} · {booking.nights} nights · {booking.adults}A · {booking.children}C · {formatTZS(booking.total_amount)}</p>
              </div>
              <div className="flex items-center gap-2">
                <Badge className={booking.payment_status === "paid" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}>Payment: {booking.payment_status}</Badge>
                <Badge className={booking.status === "checked_in" ? "bg-emerald-100 text-emerald-700" : "bg-blue-100 text-blue-700"}>{booking.status.replace("_", " ")}</Badge>
              </div>
            </div>

            {booking.payment_status !== "paid" && (
              <div className="mb-4 flex items-center justify-between p-3 rounded-lg bg-amber-50 border border-amber-200">
                <p className="text-sm text-amber-800">Payment is still pending. Confirm before check-in if needed.</p>
                <Button onClick={confirmPayment} disabled={confirmingPayment} size="sm" className="bg-emerald-600 hover:bg-emerald-700" data-testid="confirm-payment-checkin"><CheckCircle2 className="w-4 h-4 mr-1" /> Confirm payment</Button>
              </div>
            )}

            <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Assign room</Label>
                <Select value={unitId} onValueChange={setUnitId}>
                  <SelectTrigger data-testid="checkin-unit-select"><SelectValue placeholder="Pick a room" /></SelectTrigger>
                  <SelectContent>
                    {availableUnits.length === 0 && <SelectItem value="_none" disabled>No ready rooms for these dates — check Housekeeping</SelectItem>}
                    {availableUnits.map((u) => <SelectItem key={u.id} value={u.id}>Room {u.room_number} {u.floor ? `· Floor ${u.floor}` : ""}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="flex items-center gap-1"><Users className="w-3.5 h-3.5" /> Number of guests staying in the room</Label>
                <Input type="number" min="1" max="6" value={numGuests} onChange={(e) => setNumGuests(e.target.value)} data-testid="checkin-num-guests" />
              </div>

              {enabledFields.map((f) => (
                <div key={f.key} className={f.key === "address" || f.key === "purpose_of_visit" ? "sm:col-span-2" : ""}>
                  <Label>{f.label}{f.required && <span className="text-red-500 ml-1">*</span>}</Label>
                  {f.options ? (
                    <Select value={fields[f.key] || ""} onValueChange={(v) => setFields({ ...fields, [f.key]: v })}>
                      <SelectTrigger data-testid={`checkin-field-${f.key}`}><SelectValue placeholder={`Select ${f.label.toLowerCase()}`} /></SelectTrigger>
                      <SelectContent>{f.options.map((o) => <SelectItem key={o} value={o}>{o.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                    </Select>
                  ) : (
                    <Input value={fields[f.key] || ""} onChange={(e) => setFields({ ...fields, [f.key]: e.target.value })} required={f.required} data-testid={`checkin-field-${f.key}`} />
                  )}
                </div>
              ))}

              <div className="sm:col-span-2">
                <Label>ID photo <span className="text-red-500">*</span></Label>
                <div className="flex items-center gap-3 mt-1">
                  <label className="cursor-pointer flex items-center gap-2 px-3 py-2 rounded-lg border border-[color:var(--brand)]/30 hover:bg-[color:var(--sand)] text-sm">
                    {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                    <span>{idPhotoPath ? "Replace photo" : "Take/upload photo"}</span>
                    <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => uploadPhoto(e.target.files?.[0])} data-testid="checkin-photo-input" />
                  </label>
                  {idPhotoPath && (
                    <>
                      <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${idPhotoPath}`} alt="ID" className="w-14 h-14 object-cover rounded-lg border" />
                      <a href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${idPhotoPath}`} target="_blank" rel="noreferrer" className="text-xs font-ui underline text-[color:var(--brand)]">view</a>
                    </>
                  )}
                </div>
              </div>

              <div className="sm:col-span-2 flex justify-end pt-4">
                <Button type="submit" disabled={submitting || booking.status === "checked_in"} className="bg-[color:var(--brand)] hover:bg-[color:var(--brand-dark)]" data-testid="checkin-submit">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><ClipboardCheck className="w-4 h-4 mr-1" /> Complete check-in</>}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
